import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Receipt, IndianRupee } from 'lucide-react';
import Layout from '@/components/Layout';

export default function CollectRentPage() {
  const [pgs, setPgs] = useState([]);
  const [selectedPg, setSelectedPg] = useState('');
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tenantId: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Cash',
    notes: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPgs();
  }, []);

  useEffect(() => {
    if (selectedPg) {
      fetchTenants();
    }
  }, [selectedPg]);

  const fetchPgs = async () => {
    try {
      const response = await api.getPGs();
      setPgs(response.data);
      if (response.data.length > 0) {
        setSelectedPg(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to fetch PGs');
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await api.getTenants(selectedPg);
      const activeTenants = response.data.filter(t => t.status === 'active');
      setTenants(activeTenants);
      if (activeTenants.length > 0) {
        setFormData(prev => ({ ...prev, tenantId: activeTenants[0].id }));
      }
    } catch (error) {
      toast.error('Failed to fetch tenants');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createPayment({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Payment recorded successfully!');
      setFormData({
        ...formData,
        amount: '',
        notes: ''
      });
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const selectedTenant = tenants.find(t => t.id === formData.tenantId);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 font-heading">Collect Rent</h1>
          <p className="text-slate-600 mt-1">Record tenant payments</p>
        </div>

        {tenants.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">No Active Tenants</h2>
            <p className="text-slate-600 mb-4">Add tenants first to collect rent</p>
            <Button onClick={() => navigate('/tenants/add')} data-testid="add-tenant-redirect-button">
              Add Tenant
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 font-heading">Payment Details</h2>
              </div>

              {pgs.length > 1 && (
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Select PG</Label>
                  <select
                    value={selectedPg}
                    onChange={(e) => setSelectedPg(e.target.value)}
                    className="h-12 w-full px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20"
                    data-testid="payment-pg-select"
                  >
                    {pgs.map((pg) => (
                      <option key={pg.id} value={pg.id}>{pg.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Select Tenant *</Label>
                <select
                  value={formData.tenantId}
                  onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                  className="h-12 w-full px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20"
                  required
                  data-testid="payment-tenant-select"
                >
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name} - {tenant.phone}
                    </option>
                  ))}
                </select>
              </div>

              {selectedTenant && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-blue-700 font-medium mb-1">Monthly Rent</p>
                      <p className="text-blue-900 font-bold text-lg">₹{selectedTenant.monthlyRent.toLocaleString('en-IN')}</p>
                    </div>
                    <div>
                      <p className="text-blue-700 font-medium mb-1">Current Balance</p>
                      <p className={`font-bold text-lg ${selectedTenant.balance >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                        ₹{Math.abs(selectedTenant.balance).toLocaleString('en-IN')}
                        {selectedTenant.balance < 0 ? ' Due' : ' Advance'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Payment Amount (₹) *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="h-14 pl-10 rounded-lg text-lg font-semibold"
                    required
                    min="1"
                    data-testid="payment-amount-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Payment Date *</Label>
                  <Input
                    type="date"
                    value={formData.paymentDate}
                    onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                    className="h-12 rounded-lg"
                    required
                    data-testid="payment-date-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Payment Method *</Label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="h-12 w-full px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20"
                    data-testid="payment-method-select"
                  >
                    <option>Cash</option>
                    <option>UPI</option>
                    <option>Bank Transfer</option>
                    <option>Cheque</option>
                    <option>Card</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Notes (Optional)</Label>
                <Input
                  placeholder="Any additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="h-12 rounded-lg"
                  data-testid="payment-notes-input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
                data-testid="payment-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold"
                disabled={loading}
                data-testid="payment-submit-button"
              >
                {loading ? 'Recording...' : 'Record Payment'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
}
